import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  createTenant,
  listAvailableVerticals,
  listTenants,
  type AvailableVertical,
  type CreateTenantBody,
  type TenantSummary,
} from "@/lib/api";
import {
  Badge,
  Button,
  ErrorBanner,
  Field,
  Modal,
  PageContainer,
  Select,
  TextInput,
  toErrorMessage,
} from "@/components/Forms";
import { PlusIcon } from "@/components/Icons";
import { verticalLabel } from "@/lib/verticals";

const DISTRIBUTORS = ["direct", "agency", "partner"];
const LOCALES = ["sv", "en", "no", "fi"];
/** Fallback used only if `GET /admin-api/verticals` errors — keeps the modal openable in degraded states. */
const FALLBACK_VERTICALS: AvailableVertical[] = [{ key: "fitness", hasPack: true }];

export default function TenantsPage() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<TenantSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [reloadAt, setReloadAt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    listTenants()
      .then((r) => !cancelled && setRows(r))
      .catch((e) => !cancelled && setError(toErrorMessage(e, t("tenants.loadError"))));
    return () => {
      cancelled = true;
    };
  }, [reloadAt, t]);

  return (
    <PageContainer
      title={t("tenants.title")}
      subtitle={t("tenants.subtitle")}
      actions={
        <Button variant="primary" onClick={() => setCreating(true)}>
          <PlusIcon size={14} /> {t("tenants.newTenant")}
        </Button>
      }
    >
      <ErrorBanner message={error} />

      {rows === null ? (
        <SkeletonRows />
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("tenants.empty")}</p>
      ) : (
        <div className="rounded-lg border border-border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2">{t("tenants.colSlug")}</th>
                <th className="text-left px-3 py-2">{t("tenants.colName")}</th>
                <th className="text-left px-3 py-2 hidden md:table-cell">{t("tenants.colVertical")}</th>
                <th className="text-left px-3 py-2 hidden md:table-cell">{t("tenantDetail.factDistributor")}</th>
                <th className="text-left px-3 py-2">{t("tenants.colStatus")}</th>
                <th className="text-left px-3 py-2 hidden lg:table-cell">{t("tenants.colDb")}</th>
                <th className="text-right px-3 py-2">{t("tenants.colCreated")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((tenant) => (
                <tr key={tenant.slug} className="hover:bg-muted/30">
                  <td className="px-3 py-2">
                    <Link
                      to={`/tenants/${tenant.slug}`}
                      className="font-mono text-accent hover:underline"
                    >
                      {tenant.slug}
                    </Link>
                  </td>
                  <td className="px-3 py-2">{tenant.name}</td>
                  <td className="px-3 py-2 hidden md:table-cell text-muted-foreground">
                    {tenant.vertical ? verticalLabel(tenant.vertical, t) : "—"}
                  </td>
                  <td className="px-3 py-2 hidden md:table-cell text-muted-foreground">
                    {tenant.distributor}
                  </td>
                  <td className="px-3 py-2">
                    <Badge tone={tenant.status === "ACTIVE" ? "success" : "warning"}>
                      {tenant.status ?? "—"}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 hidden lg:table-cell font-mono text-xs text-muted-foreground">
                    {tenant.dbName}
                  </td>
                  <td className="px-3 py-2 text-right text-xs text-muted-foreground">
                    {tenant.createdAt ? tenant.createdAt.slice(0, 10) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateTenantModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={() => {
          setCreating(false);
          setReloadAt((n) => n + 1);
        }}
      />
    </PageContainer>
  );
}

function CreateTenantModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [available, setAvailable] = useState<AvailableVertical[]>(FALLBACK_VERTICALS);
  /**
   * Picked vertical keys, in insertion order. First entry is the primary
   * (matches the backend invariant `verticals[0] === vertical`). Stored as
   * an array (not a Set) so React diffs cleanly and the "Primary" badge
   * always renders next to the same element across re-renders.
   */
  const [pickedVerticals, setPickedVerticals] = useState<string[]>(["fitness"]);
  const [form, setForm] = useState<Omit<CreateTenantBody, "vertical" | "verticals">>({
    slug: "",
    name: "",
    distributor: "direct",
    locale: "sv",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setForm({
      slug: "",
      name: "",
      distributor: "direct",
      locale: "sv",
    });
    setErr(null);
    listAvailableVerticals()
      .then((list) => {
        if (cancelled) return;
        const usable = list.length > 0 ? list : FALLBACK_VERTICALS;
        setAvailable(usable);
        // Default-pick the first available so the user can hit Create
        // immediately for the common single-vertical case.
        setPickedVerticals([usable[0]?.key ?? "fitness"]);
      })
      .catch(() => {
        if (cancelled) return;
        setAvailable(FALLBACK_VERTICALS);
        setPickedVerticals([FALLBACK_VERTICALS[0]?.key ?? "fitness"]);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  function toggleVertical(key: string) {
    setPickedVerticals((prev) => {
      if (prev.includes(key)) {
        const next = prev.filter((k) => k !== key);
        // Don't allow zero — the user needs at least one vertical. We
        // leave the last-checked sticky; submit() also catches this.
        return next.length === 0 ? prev : next;
      }
      return [...prev, key];
    });
  }

  async function submit() {
    const slug = form.slug.trim().toLowerCase();
    if (!slug) {
      setErr(t("tenants.errorSlugRequired"));
      return;
    }
    if (!/^[a-z0-9-]+$/.test(slug)) {
      setErr(t("tenants.errorSlugFormat"));
      return;
    }
    if (!form.name.trim()) {
      setErr(t("tenants.errorNameRequired"));
      return;
    }
    if (pickedVerticals.length === 0) {
      setErr(t("tenants.errorVerticalRequired"));
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await createTenant({
        ...form,
        slug,
        name: form.name.trim(),
        verticals: pickedVerticals,
        // Keep `vertical` populated for back-compat readers; the backend
        // re-derives this anyway, but sending it makes the wire payload
        // self-consistent for log readers.
        vertical: pickedVerticals[0],
      });
      onCreated();
    } catch (e) {
      setErr(toErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      title={t("tenants.modalTitle")}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            {t("forms.cancel")}
          </Button>
          <Button variant="primary" onClick={submit} disabled={busy}>
            {busy ? t("tenants.creating") : t("tenants.create")}
          </Button>
        </>
      }
    >
      <ErrorBanner message={err} />
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("tenants.fieldSlug")} required hint={t("tenants.fieldSlugHint")}>
            <TextInput
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              placeholder="beta"
              autoFocus
            />
          </Field>
          <Field label={t("tenants.fieldName")} required>
            <TextInput
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Beta Gym"
            />
          </Field>
        </div>
        <Field label={t("tenants.fieldVerticals")} required hint={t("tenants.fieldVerticalsHint")}>
          <div className="space-y-1.5 rounded-md border border-border bg-card/30 p-2">
            {available.map((v) => {
              const checked = pickedVerticals.includes(v.key);
              const isPrimary = checked && pickedVerticals[0] === v.key;
              return (
                <label
                  key={v.key}
                  className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/30 rounded px-1 py-1"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleVertical(v.key)}
                  />
                  <span className="flex-1">{verticalLabel(v.key, t)}</span>
                  {isPrimary && (
                    <Badge tone="success">{t("tenants.verticalPrimaryBadge")}</Badge>
                  )}
                </label>
              );
            })}
          </div>
        </Field>
        <Field label={t("tenants.fieldDistributor")} hint={t("tenants.fieldDistributorHint")} required>
          <Select value={form.distributor} onChange={(e) => setForm({ ...form, distributor: e.target.value })}>
            {DISTRIBUTORS.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </Select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Brand">
            <TextInput
              value={form.brandName ?? ""}
              onChange={(e) => setForm({ ...form, brandName: e.target.value || null })}
              placeholder="Beta Gym"
            />
          </Field>
          <Field label={t("tenants.fieldLocale")} hint={t("tenants.fieldLocaleHint")}>
            <Select value={form.locale ?? "sv"} onChange={(e) => setForm({ ...form, locale: e.target.value })}>
              {LOCALES.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <Field label="From email">
          <TextInput
            type="email"
            value={form.fromEmail ?? ""}
            onChange={(e) => setForm({ ...form, fromEmail: e.target.value || null })}
            placeholder="no-reply@beta.example"
          />
        </Field>
        <Field label="SMS sender id">
          <TextInput
            value={form.senderIdSms ?? ""}
            onChange={(e) => setForm({ ...form, senderIdSms: e.target.value || null })}
            placeholder="+46710000000"
          />
        </Field>
      </div>
    </Modal>
  );
}

function SkeletonRows() {
  return (
    <div className="space-y-2">
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-12 rounded-md bg-card/60 animate-pulse" />
      ))}
    </div>
  );
}
